import {
	Modal,
	ModalHeader,
	ModalContent,
	ModalOverlay,
	ModalFooter,
	Box,
	Flex,
	Input,
	Spinner,
	Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Button } from "newui/components/Button";
import { Conversation, CreateConversation } from "./types/Conversation.type";
import { MessageComposer } from "./components/MessageComposer";
import { ProcuraconContextType } from "newui/types/ContextType.type";
import { UploadedFile } from "newui/components/FileUpload";
import { createConversation } from "./api";
import { LengthOfHtmlTextString } from "helpers/stringHelper";
import { logErrorIfDebug } from "utils/logging";

interface CreateConversationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConversationCreated?: (conversation: Conversation) => void;
	additionalContextId?: number;
	contextId: number;
	contextType: ProcuraconContextType;
	defaultSubject?: string;
	extraData?: string;
}

export const CreateConversationModal: React.FC<
	CreateConversationModalProps
> = ({
	isOpen,
	onClose,
	onConversationCreated,
	additionalContextId,
	contextId,
	contextType,
	defaultSubject = "",
	extraData = "",
}) => {
	const [subject, setSubject] = useState(defaultSubject);
	const [isSaving, setIsSaving] = useState(false);
	const [messageBody, setMessageBody] = useState("");
	const [files, setFiles] = useState<UploadedFile[] | null>(null);
	const [errors, setErrors] = useState<{ subject?: string; message?: string }>(
		{}
	);

	const validate = () => {
		const newErrors: { subject?: string; message?: string } = {};

		const subjectLength = subject?.trim()?.length ?? 0;

		if (subjectLength === 0) {
			newErrors.subject = "Subject is required.";
		} else if (subjectLength > 255) {
			newErrors.subject = `Subject must be less than 255 characters (current ocunt is ${subjectLength}).`;
		}

		if (LengthOfHtmlTextString(messageBody) === 0) {
			newErrors.message = "Message body is required.";
		}

		setErrors(newErrors);

		// Return true if no errors
		return Object.keys(newErrors).length === 0;
	};

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setSubject(defaultSubject);
			setMessageBody("");
			setFiles(null);
			setIsSaving(false);
		}
	}, [isOpen, defaultSubject]);

	// Conversation creation logic
	useEffect(() => {
		if (!isSaving) {
			return;
		}

		const isGuestContext = typeof contextId === "string";

		const submissionData: CreateConversation = {
			additional_context_id: additionalContextId,
			context_id: isGuestContext ? null : contextId,
			guest_invite_uuid: isGuestContext ? contextId : null,
			context_type_id: contextType,
			body: messageBody,
			subject: subject,
			files: files ? files.map((file) => file.id) : undefined,
		};

		createConversation(submissionData)
			.then((conversation) => {
				if (!conversation) {
					return;
				}

				onConversationCreated(conversation);
				onClose();
			})
			.catch((error) => {
				logErrorIfDebug(error);
				// error so do nothing
			})
			.finally(() => setIsSaving(false));
	}, [
		isSaving,
		subject,
		messageBody,
		contextId,
		contextType,
		extraData,
		onConversationCreated,
		onClose,
	]);

	const handleCreate = () => {
		if (!validate()) {
			return;
		}
		setIsSaving(true);
	};

	const handleClose = () => {
		if (!isSaving) {
			onClose();
		}
	};

	const onBodyChange = (value: string) => {
		setMessageBody(value);
		validate();
	};

	const onSubjectChange = (e) => {
		setSubject(e.target.value);
		validate();
	};

	return (
		<Modal
			isCentered
			isOpen={isOpen}
			onClose={handleClose}
			closeOnOverlayClick={false}
		>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Create New Conversation</ModalHeader>
				<Flex
					p={2}
					gap={2}
					align="stretch"
					direction="column"
				>
					<Box
						pl={2}
						minW="80px"
						fontSize="sm"
						fontWeight="medium"
						textAlign="left"
					>
						Subject
					</Box>

					<Input
						placeholder="Subject"
						value={subject}
						onChange={onSubjectChange}
						disabled={isSaving}
						max={255}
						className="focus-visible:!border-blue-400 focus-visible:!shadow-none"
					/>
					{errors.subject && (
						<Text
							fontSize="sm"
							pl={2}
							className="text-red-500"
						>
							{errors.subject}
						</Text>
					)}

					<Box
						pt={2}
						pl={2}
						minW="80px"
						fontSize="sm"
						fontWeight="medium"
						textAlign="left"
					>
						Message
					</Box>

					<MessageComposer
						conversationId={0}
						hideSendButton
						disabled={isSaving}
						onBodyChanged={onBodyChange}
						onFilesChanged={setFiles}
					/>
					{errors.message && (
						<Text
							fontSize="sm"
							pl={2}
							className="text-red-500"
						>
							{errors.message}
						</Text>
					)}
				</Flex>

				<ModalFooter>
					<Button
						btntype="secondary"
						type="button"
						className="mr-4"
						onClick={handleClose}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						type="submit"
						onClick={handleCreate}
						disabled={isSaving || !subject.trim() || !messageBody.trim()}
					>
						{isSaving ? (
							<Flex
								align="center"
								gap={2}
							>
								<Spinner size="sm" />
								Creating...
							</Flex>
						) : (
							"Create Conversation"
						)}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
